import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function DateNightRestaurantsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Date Night Restaurant Picker — Find the Perfect Spot Together | Hangrily"
        description="Planning date night? Both you and your partner swipe through nearby restaurants independently — Hangrily reveals only the places you both loved. Filter by cuisine, price & mood. Better than scrolling Yelp together. Free."
        path="/date-night-restaurants"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Date Night Restaurant Picker - Hangrily',
            description: 'Find the perfect date night restaurant by swiping with your partner. Match on restaurants you both love.',
            url: 'https://hangrily.app/date-night-restaurants'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Date Night Restaurants', item: 'https://hangrily.app/date-night-restaurants' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do couples decide where to eat without arguing?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Give both people equal, private input. Hangrily's Couple Mode has both partners swipe on restaurants independently — neither sees the other's picks. The app reveals only mutual favorites. Since the result is a place you both genuinely liked, there's nothing to argue about."
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily better than "you pick" or a coin flip for date night?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '"You pick" puts all the pressure on one person, and coin flips are random — you might land somewhere neither of you wanted. Hangrily ensures the result is a restaurant you both independently swiped right on. That\'s real consensus, not a gamble.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I plan a surprise date night with Hangrily?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Use Solo Mode with the Romantic mood filter and an upscale price range. Swipe through the curated deck, pick a winner, and surprise your partner with a reservation. You can also save places to your Favorites to build a go-to list of date night spots.'
                }
              },
              {
                '@type': 'Question',
                name: 'What if we don\'t match on any restaurants?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'If your tastes are really different that night, Hangrily still ranks restaurants by how close you were to agreement. You\'ll see the "almost matched" options ranked by combined interest, so you still get a solid recommendation even without a perfect match.'
                }
              },
              {
                '@type': 'Question',
                name: 'Does Couple Mode work if we\'re not in the same room?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes — both partners just need to be online. But for the best results, use it when you\'re in the same area so the restaurant results are relevant to where you\'ll actually be eating tonight.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Date Night Restaurant Picker for Couples</h1>
          <p className="hero-subtitle">
            Both of you swipe. The app finds restaurants you both love. Argument over.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '28px', flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
              Try Couple Mode Free
            </button>
            <button className="btn btn-secondary btn-lg" onClick={() => navigate('/couples-restaurant-picker')}>
              Learn About Couple Mode
            </button>
          </div>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Why Date Night Restaurant Decisions Are So Hard</h2>
              <p>
                It's Friday evening. You're both hungry. The conversation goes: <em>"Where do you want to eat?"</em> → 
                <em>"I don't know, you pick."</em> → <em>"No, you pick."</em> → 20 minutes later you're still on the couch, 
                hangrier than when you started.
              </p>
              <p>
                A 2023 study from Ohio State found that <strong>couples spend an average of 5.4 days per year arguing about 
                food decisions</strong>. Random pickers and wheel spinners don't solve the core problem — they still 
                leave one person eating somewhere they didn't choose.
              </p>
              <p>
                Hangrily's Couple Mode is different. Both partners open the app and swipe through restaurant cards 
                independently — real photos, Google ratings, price levels, and walking distance. <strong>Neither person 
                sees what the other chose until both finish.</strong> The app then reveals only the restaurants you both 
                swiped right on. Your top mutual match is tonight's dinner.
              </p>
            </div>
            <div className="column-features">
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">�</span>
                <h3>Blind Swiping</h3>
                <p>Neither partner sees the other's choices. No influence, no guilt, no passive-aggressive "fine, let's just go there."</p>
              </div>
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">❤️</span>
                <h3>Mutual Matches</h3>
                <p>You only match on restaurants you BOTH independently liked. That's genuine agreement, not compromise.</p>
              </div>
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">📸</span>
                <h3>Real Photos & Reviews</h3>
                <p>See what the food actually looks like. Google ratings, price level, hours — make informed choices together.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>Your Date Night, Planned in 3 Taps</h2>
          <div className="steps-grid">
            <div className="step-card">
              <span className="step-number">1</span>
              <h3>Set the Vibe</h3>
              <p>Pick "Romantic" mood, choose cuisines you're both open to (Italian? Sushi? Anything?), and set your budget. The app builds a curated deck of date-worthy restaurants near you.</p>
            </div>
            <div className="step-card">
              <span className="step-number">2</span>
              <h3>Both Swipe Independently</h3>
              <p>Each of you swipes through the same deck on your own phone. Right for "yes, love it" — left for "not tonight." Takes about 90 seconds each.</p>
            </div>
            <div className="step-card">
              <span className="step-number">3</span>
              <h3>Reveal Your Match</h3>
              <p>The app shows the restaurant you both loved most. Tap for directions, check the menu, or save it for next time. Date night = decided.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Date Night Ideas by Cuisine</h2>
          <p style={{ maxWidth: '700px', margin: '0 auto 32px', opacity: 0.7, textAlign: 'center' }}>
            Use Hangrily's cuisine filter to explore date-worthy restaurants by category:
          </p>
          <div className="use-case-grid">
            <Link to="/cuisine/italian" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍝</span>
              <h3>Italian Date Night</h3>
              <p>Shared pasta, a bottle of Chianti, and candlelight. The timeless romantic dinner for good reason.</p>
            </Link>
            <Link to="/cuisine/japanese" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍣</span>
              <h3>Omakase for Two</h3>
              <p>Let the chef surprise you both. Omakase and sake pairings make for an unforgettable evening.</p>
            </Link>
            <Link to="/cuisine/french" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">�</span>
              <h3>French Fine Dining</h3>
              <p>Multi-course tasting menus, wine flights, and white tablecloths. For the special-occasion date night.</p>
            </Link>
            <Link to="/cuisine/thai" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🍜</span>
              <h3>Thai & Cocktails</h3>
              <p>Pad Thai, curries, and craft cocktails at trendy Thai spots. Casual yet elevated.</p>
            </Link>
            <Link to="/cuisine/korean" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🥩</span>
              <h3>Korean BBQ Date</h3>
              <p>Grill together at the table. Interactive, fun, and inherently a couple's activity.</p>
            </Link>
            <Link to="/cuisine/mediterranean" className="use-case-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span className="use-case-emoji">🫒</span>
              <h3>Mediterranean Mezze</h3>
              <p>Shared small plates, hummus, grilled lamb, and outdoor seating. Relaxed and intimate.</p>
            </Link>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>Why Couples Love Hangrily for Date Night</h2>
          <div className="comparison-table">
            <div className="compare-row compare-header">
              <div className="compare-feature">The Old Way</div>
              <div className="compare-them">What Happens</div>
              <div className="compare-us">Hangrily Instead</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">"You pick"</div>
              <div className="compare-them">😩 One person carries the burden, gets vetoed</div>
              <div className="compare-us">✅ Both swipe — shared decision</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Scrolling Yelp together</div>
              <div className="compare-them">😩 30 min of "what about this one? no..."</div>
              <div className="compare-us">✅ 90 seconds of independent swiping</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Flipping a coin</div>
              <div className="compare-them">😩 Random pick neither of you wanted</div>
              <div className="compare-us">✅ Match = you both wanted it</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Going to "the usual"</div>
              <div className="compare-them">😩 Boring, never try anything new</div>
              <div className="compare-us">✅ Discover new spots together</div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Date Night Questions, Answered</h2>
          <div className="faq-list">
            <details className="faq-item">
              <summary>How do couples decide where to eat without arguing?</summary>
              <p>Give both people equal, private input. Hangrily's Couple Mode has both partners swipe on restaurants independently — neither sees the other's picks. The app reveals only mutual favorites. Since the result is a place you both genuinely liked, there's nothing to argue about.</p>
            </details>
            <details className="faq-item">
              <summary>Is Hangrily better than "you pick" or a coin flip for date night?</summary>
              <p>"You pick" puts all the pressure on one person, and coin flips are random — you might land somewhere neither of you wanted. Hangrily ensures the result is a restaurant you both independently swiped right on. That's real consensus, not a gamble.</p>
            </details>
            <details className="faq-item">
              <summary>Can I plan a surprise date night with Hangrily?</summary>
              <p>Absolutely. Use Solo Mode with the Romantic mood filter and an upscale price range. Swipe through the curated deck, pick a winner, and surprise your partner with a reservation. You can also save places to your Favorites to build a go-to list of date night spots.</p>
            </details>
            <details className="faq-item">
              <summary>What if we don't match on any restaurants?</summary>
              <p>If your tastes are really different that night, Hangrily still ranks restaurants by how close you were to agreement. You'll see the "almost matched" options ranked by combined interest, so you still get a solid recommendation even without a perfect match.</p>
            </details>
            <details className="faq-item">
              <summary>Does Couple Mode work if we're not in the same room?</summary>
              <p>Yes — both partners just need to be online. But for the best results, use it when you're in the same area so the restaurant results are relevant to where you'll actually be eating tonight.</p>
            </details>
          </div>
        </section>

        <section className="content-section alt-bg">
          <div className="internal-links-section">
            <h2>More for Couples & Date Night</h2>
            <div className="internal-links-grid">
              <Link to="/couples-restaurant-picker" className="internal-link">Couples Restaurant Picker</Link>
              <Link to="/where-should-i-eat" className="internal-link">Where Should I Eat?</Link>
              <Link to="/best-restaurants-near-me" className="internal-link">Restaurants Near Me</Link>
              <Link to="/what-to-eat-tonight" className="internal-link">What to Eat Tonight</Link>
              <Link to="/random-restaurant-picker" className="internal-link">Random Restaurant Picker</Link>
              <Link to="/food-decision-fatigue" className="internal-link">Food Decision Fatigue</Link>
              <Link to="/locations" className="internal-link">Browse All Cities</Link>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Plan Tonight's Date in Under 2 Minutes</h2>
          <p>Both of you swipe. The app finds the restaurant you both love. No arguments, no settling.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Start Swiping Together →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
